/**
 * 图片切割器
 * 将图片切割成六边形拼图碎片的纹理
 */
import { Vector2, HexCoord, GameConfig, EdgeDirection } from '../types';
import { HexMath } from '../core/HexMath';
import { EdgeGenerator } from '../core/EdgeGenerator';
import { TextureAtlasPacker, AtlasFrame, AtlasConfig } from '../render/TextureAtlasPacker';

export interface SlicedPiece {
    coord: HexCoord;
    texture: HTMLCanvasElement;
    pathData: string;
    center: Vector2;
    bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    edgeTypes: Map<EdgeDirection, 'tab' | 'hole' | 'flat'>;
}

// 边缘厚度效果配置
export interface BevelConfig {
    enabled: boolean;
    thickness: number;      // 厚度像素
    lightAngle: number;     // 光源角度（度），0 = 右侧，90 = 下方，默认 315（左上）
    highlightColor: string; // 高光颜色
    shadowColor: string;    // 阴影颜色
    highlightAlpha: number; // 高光透明度
    shadowAlpha: number;    // 阴影透明度
}

// 调试配置
export interface DebugConfig {
    showPieceNumber: boolean;  // 是否显示碎片编号
    fontSize: number;          // 字体大小
    fontColor: string;         // 字体颜色
    backgroundColor: string;   // 背景颜色
}

export class ImageSlicer {
    private config: GameConfig;
    private edgeGenerator: EdgeGenerator;
    private bevelConfig: BevelConfig;
    private debugConfig: DebugConfig;
    private pieceIndex: number = 0;  // 碎片编号计数器
    private worker: Worker | null = null;

    constructor(config: GameConfig, bevelConfig?: Partial<BevelConfig>, debugConfig?: Partial<DebugConfig>) {
        this.config = config;
        this.edgeGenerator = new EdgeGenerator(config);
        this.bevelConfig = {
            enabled: true,  // 默认禁用边缘厚度效果
            thickness: 1.5,
            lightAngle: 315,        // 左上方光源
            highlightColor: '#ffffff',
            shadowColor: '#000000',
            highlightAlpha: 0.4,
            shadowAlpha: 0.5,
            ...bevelConfig
        };
        this.debugConfig = {
            showPieceNumber: false,  // 默认关闭调试编号
            fontSize: 14,
            fontColor: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            ...debugConfig
        };
    }

    /**
     * 更新配置
     */
    updateConfig(config: Partial<GameConfig>): void {
        this.config = { ...this.config, ...config };
        this.edgeGenerator.updateConfig(config);
    }

    /**
     * 更新斜切厚度配置
     */
    updateBevelConfig(config: Partial<BevelConfig>): void {
        this.bevelConfig = { ...this.bevelConfig, ...config };
    }

    /**
     * 更新调试配置
     */
    updateDebugConfig(config: Partial<DebugConfig>): void {
        this.debugConfig = { ...this.debugConfig, ...config };
    }

    /**
     * 设置是否显示碎片编号（调试用）
     */
    setShowPieceNumber(show: boolean): void {
        this.debugConfig.showPieceNumber = show;
    }

    /**
     * 处理上传的图片，调整到标准尺寸
     */
    async processImage(imageSource: string | File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                resolve(img);
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            if (imageSource instanceof File) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    img.src = e.target?.result as string;
                };
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsDataURL(imageSource);
            } else {
                img.src = imageSource;
            }
        });
    }

    /**
     * 将图片调整到标准尺寸 (1024x1024)
     */
    resizeImage(img: HTMLImageElement): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const size = this.config.imageSize;

        canvas.width = size;
        canvas.height = size;

        // 计算缩放和裁剪参数（居中裁剪）
        const scale = Math.max(size / img.width, size / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const offsetX = (size - scaledWidth) / 2;
        const offsetY = (size - scaledHeight) / 2;

        // 绘制调整后的图片
        ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

        return canvas;
    }

    /**
     * 异步切割图片（使用 Web Worker）
     */
    async sliceImageAsync(sourceCanvas: HTMLCanvasElement): Promise<SlicedPiece[]> {
        // 核心修复：每次切割前强制重启 Worker，确保新难度的配置被干净地加载
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        
        if (!this.worker) {
            this.worker = new Worker(new URL('../workers/SlicerWorker.ts', import.meta.url), { type: 'module' });
        }

        const imageBitmap = await createImageBitmap(sourceCanvas);

        // 在主线程也生成边缘数据，供 SmartLayoutManager 等逻辑使用
        this.edgeGenerator.generateAllEdges();

        return new Promise((resolve, reject) => {
            const onMessage = (e: MessageEvent) => {
                const { type, pieces, error } = e.data;
                if (type === 'sliced') {
                    this.worker?.removeEventListener('message', onMessage);
                    console.log(`Web Worker 切割完成，收到 ${pieces.length} 个碎片`);

                    // 将 Worker 返回的数据转换回 SlicedPiece
                    const result: SlicedPiece[] = pieces.map((p: any) => {
                        const canvas = document.createElement('canvas');
                        canvas.width = p.bounds.width;
                        canvas.height = p.bounds.height;
                        const ctx = canvas.getContext('2d')!;
                        ctx.drawImage(p.imageBitmap, 0, 0);

                        return {
                            coord: p.coord,
                            texture: canvas,
                            pathData: p.pathData,
                            center: p.center,
                            bounds: p.bounds,
                            edgeTypes: new Map(p.edgeTypes)
                        };
                    });

                    resolve(result);
                } else if (type === 'error') {
                    this.worker?.removeEventListener('message', onMessage);
                    reject(new Error(error));
                }
            };

            this.worker?.addEventListener('message', onMessage);
            this.worker?.postMessage({
                type: 'slice',
                data: {
                    imageBitmap,
                    config: this.config
                }
            }, [imageBitmap]);
        });
    }

    /**
     * 切割图片为六边形碎片
     */
    sliceImage(sourceCanvas: HTMLCanvasElement): SlicedPiece[] {
        // 重置碎片编号计数器
        this.pieceIndex = 0;

        // 生成边缘数据
        this.edgeGenerator.generateAllEdges();

        // 获取所有有效碎片坐标
        const coords = this.edgeGenerator.getValidPieceCoords();
        const pieces: SlicedPiece[] = [];

        for (const coord of coords) {
            const piece = this.slicePiece(sourceCanvas, coord);
            if (piece) {
                pieces.push(piece);
            }
        }

        return pieces;
    }

    /**
     * 切割单个碎片
     */
    private slicePiece(sourceCanvas: HTMLCanvasElement, coord: HexCoord): SlicedPiece | null {
        const pathData = this.edgeGenerator.createPiecePath(coord);

        // 计算边缘类型
        const edgeTypes = new Map<EdgeDirection, 'tab' | 'hole' | 'flat'>();
        const edges = HexMath.getPieceEdges(coord);

        for (let i = 0; i < 6; i++) {
            const edge = edges[i];
            const direction = i as EdgeDirection;

            if (!this.edgeGenerator.hasEdge(edge.key)) {
                edgeTypes.set(direction, 'flat');
            } else {
                const flip = this.edgeGenerator.getEdgeFlip(edge.key);
                // 修正：根据用户反馈和实际渲染效果，flip === reverse 时为凸起 (tab)
                const isTab = (flip === edge.reverse);
                edgeTypes.set(direction, isTab ? 'tab' : 'hole');
            }
        }

        // 检查路径是否有效
        if (pathData.includes('NaN')) {
            return null;
        }

        // 解析路径获取边界
        const bounds = this.getPathBounds(pathData);
        if (!bounds) return null;

        // 添加边距（需要额外空间给厚度效果）
        const bevelExtra = this.bevelConfig.enabled ? this.bevelConfig.thickness + 2 : 0;
        const padding = 10 + bevelExtra;
        const x = Math.floor(bounds.minX - padding);
        const y = Math.floor(bounds.minY - padding);
        const width = Math.ceil(bounds.maxX - bounds.minX + padding * 2);
        const height = Math.ceil(bounds.maxY - bounds.minY + padding * 2);

        // 创建碎片画布
        const pieceCanvas = document.createElement('canvas');
        pieceCanvas.width = width;
        pieceCanvas.height = height;
        const ctx = pieceCanvas.getContext('2d')!;

        // 创建裁剪路径
        const path = new Path2D(pathData);

        // 平移路径到画布坐标
        ctx.translate(-x, -y);

        // 绘制斜切厚度效果（在图片下方）
        if (this.bevelConfig.enabled) {
            this.drawBevelEffect(ctx, path);
        }

        // 设置裁剪区域
        ctx.save();
        ctx.clip(path);

        // 绘制图片
        ctx.drawImage(sourceCanvas, 0, 0);

        ctx.restore();

        // 绘制边缘高光和阴影（在图片上方）
        if (this.bevelConfig.enabled) {
            this.drawEdgeHighlight(ctx, path);
        }

        // 绘制调试编号（包含坐标信息）
        if (this.debugConfig.showPieceNumber) {
            this.drawPieceNumber(ctx, width, height, coord);
        }

        // 计算中心点
        // 修正：使用逻辑中心点而不是包围盒中心，以确保吸附时的完美对齐
        const center = this.edgeGenerator.getPieceCenter(coord);

        return {
            coord,
            texture: pieceCanvas,
            pathData,
            center,
            bounds: { x, y, width, height },
            edgeTypes
        };
    }

    /**
     * 绘制碎片编号（调试用）
     * 显示格式：编号 (u,v)
     */
    private drawPieceNumber(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        coord: HexCoord
    ): void {
        const { fontSize, fontColor, backgroundColor } = this.debugConfig;
        // 显示编号和坐标，格式：编号 (u,v)
        const numberText = `${this.pieceIndex++} (${coord.u},${coord.v})`;

        ctx.save();

        // 重置变换，使用画布坐标
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // 设置字体（稍微小一点以适应更多文字）
        const adjustedFontSize = Math.max(10, fontSize - 2);
        ctx.font = `bold ${adjustedFontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 计算文本位置（画布中心）
        const centerX = width / 2;
        const centerY = height / 2;

        // 测量文本宽度
        const textMetrics = ctx.measureText(numberText);
        const textWidth = textMetrics.width;
        const textHeight = adjustedFontSize;
        const padding = 4;

        // 绘制背景圆角矩形
        const bgX = centerX - textWidth / 2 - padding;
        const bgY = centerY - textHeight / 2 - padding;
        const bgWidth = textWidth + padding * 2;
        const bgHeight = textHeight + padding * 2;
        const radius = 4;

        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        ctx.roundRect(bgX, bgY, bgWidth, bgHeight, radius);
        ctx.fill();

        // 绘制文本
        ctx.fillStyle = fontColor;
        ctx.fillText(numberText, centerX, centerY);

        ctx.restore();
    }

    /**
     * 绘制斜切厚度效果（底部阴影层）
     */
    private drawBevelEffect(ctx: CanvasRenderingContext2D, path: Path2D): void {
        const { thickness, lightAngle, shadowColor, shadowAlpha } = this.bevelConfig;

        // 计算阴影偏移方向（与光源相反）
        const angleRad = (lightAngle + 180) * Math.PI / 180;
        const offsetX = Math.cos(angleRad) * thickness;
        const offsetY = Math.sin(angleRad) * thickness;

        ctx.save();

        // 绘制多层偏移形成厚度感
        for (let i = thickness; i >= 1; i--) {
            const ratio = i / thickness;
            const ox = (offsetX * ratio);
            const oy = (offsetY * ratio);

            ctx.save();
            ctx.translate(ox, oy);

            // 颜色从深到浅渐变
            ctx.globalAlpha = shadowAlpha * (0.3 + 0.7 * ratio);
            ctx.fillStyle = shadowColor;
            ctx.fill(path);

            ctx.restore();
        }

        ctx.restore();
    }

    /**
     * 绘制边缘高光效果
     */
    private drawEdgeHighlight(ctx: CanvasRenderingContext2D, path: Path2D): void {
        const { thickness, lightAngle, highlightColor, highlightAlpha } = this.bevelConfig;

        ctx.save();

        // 高光边缘：沿着光源方向的内边缘
        ctx.globalCompositeOperation = 'source-atop';
        ctx.lineWidth = thickness * 1.5;
        ctx.strokeStyle = highlightColor;
        ctx.globalAlpha = highlightAlpha;

        // 使用 clip 限制在碎片内部
        ctx.clip(path);

        // 向光源方向偏移绘制描边，产生内侧高光
        const angleRad = lightAngle * Math.PI / 180;
        const offsetX = Math.cos(angleRad) * thickness * 0.5;
        const offsetY = Math.sin(angleRad) * thickness * 0.5;

        ctx.translate(offsetX, offsetY);
        ctx.stroke(path);

        ctx.restore();
    }

    /**
     * 计算三次贝塞尔曲线在某个 t 值的坐标
     */
    private cubicBezier(t: number, p0: number, p1: number, p2: number, p3: number): number {
        const mt = 1 - t;
        return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
    }

    /**
     * 计算三次贝塞尔曲线的精确边界
     * 通过找导数为0的点来确定极值
     */
    private getCubicBezierBounds(
        p0x: number, p0y: number,
        p1x: number, p1y: number,
        p2x: number, p2y: number,
        p3x: number, p3y: number
    ): { minX: number, minY: number, maxX: number, maxY: number } {
        let minX = Math.min(p0x, p3x);
        let maxX = Math.max(p0x, p3x);
        let minY = Math.min(p0y, p3y);
        let maxY = Math.max(p0y, p3y);

        // 计算 x 方向的极值点
        const ax = -p0x + 3 * p1x - 3 * p2x + p3x;
        const bx = 2 * p0x - 4 * p1x + 2 * p2x;
        const cx = -p0x + p1x;

        if (Math.abs(ax) > 1e-10) {
            const discriminant = bx * bx - 4 * ax * cx;
            if (discriminant >= 0) {
                const sqrtD = Math.sqrt(discriminant);
                const t1 = (-bx + sqrtD) / (2 * ax);
                const t2 = (-bx - sqrtD) / (2 * ax);
                if (t1 > 0 && t1 < 1) {
                    const x = this.cubicBezier(t1, p0x, p1x, p2x, p3x);
                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                }
                if (t2 > 0 && t2 < 1) {
                    const x = this.cubicBezier(t2, p0x, p1x, p2x, p3x);
                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                }
            }
        } else if (Math.abs(bx) > 1e-10) {
            const t = -cx / bx;
            if (t > 0 && t < 1) {
                const x = this.cubicBezier(t, p0x, p1x, p2x, p3x);
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
            }
        }

        // 计算 y 方向的极值点
        const ay = -p0y + 3 * p1y - 3 * p2y + p3y;
        const by = 2 * p0y - 4 * p1y + 2 * p2y;
        const cy = -p0y + p1y;

        if (Math.abs(ay) > 1e-10) {
            const discriminant = by * by - 4 * ay * cy;
            if (discriminant >= 0) {
                const sqrtD = Math.sqrt(discriminant);
                const t1 = (-by + sqrtD) / (2 * ay);
                const t2 = (-by - sqrtD) / (2 * ay);
                if (t1 > 0 && t1 < 1) {
                    const y = this.cubicBezier(t1, p0y, p1y, p2y, p3y);
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                }
                if (t2 > 0 && t2 < 1) {
                    const y = this.cubicBezier(t2, p0y, p1y, p2y, p3y);
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                }
            }
        } else if (Math.abs(by) > 1e-10) {
            const t = -cy / by;
            if (t > 0 && t < 1) {
                const y = this.cubicBezier(t, p0y, p1y, p2y, p3y);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }

        return { minX, minY, maxX, maxY };
    }

    /**
     * 解析 SVG 路径获取精确边界
     * 使用贝塞尔曲线的数学特性计算真实边界，而不是使用控制点
     */
    private getPathBounds(pathData: string): {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
    } | null {
        const commands = pathData.split(/(?=[MLCZ])/);
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        let currentX = 0, currentY = 0;

        for (const cmd of commands) {
            const type = cmd[0];
            const args = cmd.slice(1).trim().split(/[\s,]+/).map(parseFloat);

            if (type === 'M' || type === 'L') {
                for (let i = 0; i < args.length; i += 2) {
                    const x = args[i];
                    const y = args[i + 1];
                    if (isNaN(x) || isNaN(y)) continue;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                    currentX = x;
                    currentY = y;
                }
            } else if (type === 'C') {
                for (let i = 0; i < args.length; i += 6) {
                    const c1x = args[i];
                    const c1y = args[i + 1];
                    const c2x = args[i + 2];
                    const c2y = args[i + 3];
                    const x = args[i + 4];
                    const y = args[i + 5];

                    if (!isNaN(c1x) && !isNaN(c1y) && !isNaN(c2x) && !isNaN(c2y) && !isNaN(x) && !isNaN(y)) {
                        const curveBounds = this.getCubicBezierBounds(
                            currentX, currentY,
                            c1x, c1y,
                            c2x, c2y,
                            x, y
                        );
                        minX = Math.min(minX, curveBounds.minX);
                        minY = Math.min(minY, curveBounds.minY);
                        maxX = Math.max(maxX, curveBounds.maxX);
                        maxY = Math.max(maxY, curveBounds.maxY);
                        currentX = x;
                        currentY = y;
                    }
                }
            }
        }

        if (minX === Infinity) return null;
        return { minX, minY, maxX, maxY };
    }

    /**
     * 创建碎片的遮罩纹理（用于 WebGL）
     */
    createMaskTexture(pathData: string, width: number, height: number, offsetX: number, offsetY: number): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;

        const path = new Path2D(pathData);

        ctx.translate(-offsetX, -offsetY);

        // 填充白色作为遮罩
        ctx.fillStyle = 'white';
        ctx.fill(path);

        return canvas;
    }

    /**
     * 获取边缘生成器（供外部使用）
     */
    getEdgeGenerator(): EdgeGenerator {
        return this.edgeGenerator;
    }

    /**
     * 导出图片为 base64
     */
    canvasToBase64(canvas: HTMLCanvasElement): string {
        return canvas.toDataURL('image/png');
    }

    /**
     * 从 base64 创建画布
     */
    async base64ToCanvas(base64: string): Promise<HTMLCanvasElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d')!;
                ctx.drawImage(img, 0, 0);
                resolve(canvas);
            };
            img.onerror = reject;
            img.src = base64;
        });
    }

    /**
     * 切割图片并打包到纹理图集
     * @param sourceCanvas 源图片画布
     * @param atlasConfig 图集配置（可选）
     * @returns 包含切割碎片和图集打包器的结果
     */
    sliceImageWithAtlas(
        sourceCanvas: HTMLCanvasElement,
        atlasConfig?: Partial<AtlasConfig>
    ): {
        pieces: SlicedPiece[];
        atlasPacker: TextureAtlasPacker;
        frames: Map<string, AtlasFrame>;
    } {
        // 先切割所有碎片
        const pieces = this.sliceImage(sourceCanvas);

        // 创建图集打包器
        const atlasPacker = new TextureAtlasPacker(atlasConfig);

        // 将碎片纹理打包到图集
        const packData = pieces.map((piece) => ({
            id: `${piece.coord.u}_${piece.coord.v}`,
            texture: piece.texture
        }));

        atlasPacker.packAll(packData);

        // 获取打包后的帧数据
        const frames = atlasPacker.getAllFrames();

        return {
            pieces,
            atlasPacker,
            frames
        };
    }

    /**
     * 获取碎片在图集中的 UV 坐标（用于 WebGL 渲染）
     * @param frame 图集帧数据
     * @param atlasWidth 图集宽度
     * @param atlasHeight 图集高度
     */
    getAtlasUV(
        frame: AtlasFrame,
        atlasWidth: number,
        atlasHeight: number
    ): {
        u0: number;
        v0: number;
        u1: number;
        v1: number;
    } {
        return {
            u0: frame.x / atlasWidth,
            v0: frame.y / atlasHeight,
            u1: (frame.x + frame.width) / atlasWidth,
            v1: (frame.y + frame.height) / atlasHeight
        };
    }

    /**
     * 销毁资源
     */
    destroy(): void {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
    }
}
